/**
 * supabase.ts — Typisierter Supabase-Client + Auth-Helpers
 *
 * DSGVO-Konstraints:
 *   - URL MUSS auf eu-central-1 (Frankfurt) zeigen. Validation in createClient.
 *   - Auth-Methoden: Magic-Link (signInWithOtp) und E-Mail+Passwort
 *     (signInWithPassword/signUp/resetPasswordForEmail).
 *   - KEIN signInWithOAuth() — Social Login ist eine bewusste DSGVO-
 *     Entscheidung (2026-07-03): Bei Mandanten (SGB-II-Leistungsbezug)
 *     würde ein OAuth-Provider indirekt vom Leistungsbezug erfahren.
 *   - Keine Telemetrie an Supabase-eigene Telemetrie-Endpoints (per Default
 *     ist keine aktiv im supabase-js v2; falls künftig opt-out nötig:
 *     hier explizit deaktivieren).
 *
 * Erwartete env-vars (apps/web/.env, .env.example als Template):
 *   - VITE_SUPABASE_URL      = https://<project>.supabase.co (EU)
 *   - VITE_SUPABASE_ANON_KEY = anon-public-key (EU-Projekt)
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';

import type { Database } from './supabaseTypes';

type SupaClient = SupabaseClient<Database>;

// -----------------------------------------------------------------------------
// Lazy client init: fail loud on missing/invalid env, but only at first use.
// Erlaubt Storybook/Vitest-Imports ohne env-Setup.
// -----------------------------------------------------------------------------
let cachedClient: SupaClient | null = null;

function readEnv(): { url: string; anonKey: string } {
  // Vite-injected env: typed via vite/client.d.ts; cast unknown here to be
  // explicit and to satisfy strict no-unsafe-assignment.
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;

  if (typeof url !== 'string' || url.length === 0) {
    throw new Error(
      'VITE_SUPABASE_URL fehlt. .env aus .env.example ableiten und EU-Projekt-URL eintragen.',
    );
  }
  if (typeof anonKey !== 'string' || anonKey.length === 0) {
    throw new Error('VITE_SUPABASE_ANON_KEY fehlt.');
  }

  // EU-Hosting-Sanity-Check: typische Supabase-EU-URLs enthalten "supabase.co"
  // und werden zur Region eu-central-1 in Production zugeordnet.
  // Dies ist ein soft-check — die echte Region steht im Supabase Dashboard.
  // Lokale Dev-Instanzen (127.0.0.1, localhost) sind erlaubt.
  const isLocal = /^https?:\/\/(127\.0\.0\.1|localhost)/.test(url);
  const isSupabaseHosted = /supabase\.co|supabase\.(in|red)/.test(url);
  if (!isLocal && !isSupabaseHosted) {
    console.warn(
      `[supabase.ts] URL "${url}" sieht nicht nach Supabase-EU-Host aus. ` +
        `Bitte Region im Supabase Dashboard verifizieren: muss eu-central-1 sein.`,
    );
  }

  return { url, anonKey };
}

export function getSupabaseClient(): SupaClient {
  if (cachedClient) return cachedClient;
  const { url, anonKey } = readEnv();
  cachedClient = createClient<Database>(url, anonKey, {
    auth: {
      // Magic-Link-Flow: persistente Session in localStorage, auto-refresh
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // PKCE-Flow für zusätzliche Sicherheit (verhindert auth-code-interception)
      flowType: 'pkce',
    },
    global: {
      // Default-Headers können hier ergänzt werden (z.B. x-request-id)
    },
  });
  return cachedClient;
}

// -----------------------------------------------------------------------------
// Auth-Helpers — Magic-Link only
// -----------------------------------------------------------------------------

export interface SignInResult {
  ok: boolean;
  error?: string;
}

/**
 * Sendet einen Magic-Link an die angegebene E-Mail-Adresse.
 * Bei Erfolg klickt der User den Link in der E-Mail und wird auf die
 * App umgeleitet (siehe `auth.email.template.magic_link` in supabase/config.toml).
 */
export async function signInWithMagicLink(email: string): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
      // Auf welche Route wird der User nach Klick umgeleitet
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Verifiziert einen 6-stelligen OTP-Code aus der Magic-Link-Mail.
 *
 * Hintergrund: Supabase verschickt mit jedem Magic-Link parallel auch einen
 * 6-stelligen Code ({{ .Token }} im Email-Template). Der Code ist ein
 * Shared-Secret zwischen Server und Mail-Empfänger und braucht — anders
 * als der Link — KEINEN PKCE-code-verifier im Browser-Storage.
 *
 * Use-Case: Wenn der User die Mail in einem anderen Browser/Mail-Client
 * öffnet als die Login-Seite, scheitert der Link am fehlenden Verifier
 * ("PKCE code verifier not found"). Der Code dagegen funktioniert immer,
 * solange der Login-Tab noch offen ist.
 *
 * Sicherheit: gleich stark wie der Link — beide werden per E-Mail an die
 * vom User behauptete Adresse zugestellt. Brute-Force ist durch Rate-
 * Limiting in Supabase abgesichert (Default 30 Versuche/Stunde/IP).
 */
export async function verifyOtpCode(email: string, token: string): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.verifyOtp({
    email: email.trim().toLowerCase(),
    token: token.trim(),
    type: 'email',
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

// -----------------------------------------------------------------------------
// Auth-Helpers — E-Mail + Passwort
// -----------------------------------------------------------------------------

/**
 * Login mit E-Mail + Passwort. Session wird bei Erfolg vom Client gesetzt
 * (onAuthStateChange feuert, useSession aktualisiert).
 */
export async function signInWithPassword(email: string, password: string): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Registrierung mit E-Mail + Passwort. Mit `enable_confirmations = true`
 * (config.toml) verschickt Supabase eine Bestätigungs-Mail; die Session
 * entsteht erst nach Klick auf den Bestätigungslink (→ /auth/callback).
 *
 * Hinweis: Bei bereits existierender E-Mail antwortet Supabase bewusst
 * obfuskiert (kein Fehler) — Bestands-User setzen ihr Passwort über den
 * Recovery-Flow („Passwort vergessen oder noch keines festgelegt?“).
 */
export async function signUpWithPassword(email: string, password: string): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Passwort-Reset anfordern. Der Link in der Mail führt auf /auth/reset —
 * eine DEDIZIERTE Route, weil PKCE-Recovery-Links als `?code=` ohne
 * verlässliches `type=recovery` ankommen und der normale /auth/callback
 * sie nicht von Magic-Links unterscheiden könnte.
 *
 * Deckt zugleich „Passwort erstmalig festlegen“ für Bestands-User ab,
 * die bisher nur Magic-Link genutzt haben.
 */
export async function requestPasswordReset(email: string): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    redirectTo: `${window.location.origin}/auth/reset`,
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Neues Passwort für die AKTUELLE Session setzen (nach Recovery-Exchange
 * auf /auth/reset oder aus einem eingeloggten Kontext).
 */
export async function updatePassword(newPassword: string): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.updateUser({ password: newPassword });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Recovery-OTP verifizieren — PKCE-Fallback für /auth/reset, analog zu
 * verifyOtpCode für Magic-Links: Wenn der Recovery-Link in einem anderen
 * Browser geöffnet wurde, fehlt der PKCE-Verifier; der 6-stellige Code
 * aus derselben Mail funktioniert trotzdem.
 */
export async function verifyRecoveryOtp(email: string, token: string): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.verifyOtp({
    email: email.trim().toLowerCase(),
    token: token.trim(),
    type: 'recovery',
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Beendet die Session lokal und beim Server.
 */
export async function signOut(): Promise<SignInResult> {
  const supa = getSupabaseClient();
  const { error } = await supa.auth.signOut();
  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Liest die aktuelle Session synchron aus dem In-Memory-Cache des Clients.
 * Für reaktive Komponenten besser useSession() benutzen.
 */
export async function getCurrentSession(): Promise<Session | null> {
  const supa = getSupabaseClient();
  const { data } = await supa.auth.getSession();
  return data.session;
}

// -----------------------------------------------------------------------------
// React-Hook: useSession
// -----------------------------------------------------------------------------

/**
 * Reactive session subscription. Returns the current session (or null) and
 * automatically updates when the user signs in / out / refreshes token.
 *
 * Usage:
 *   const { session, isLoading } = useSession();
 *   if (isLoading) return <Spinner />;
 *   if (!session) return <LoginScreen />;
 */
export interface UseSessionResult {
  session: Session | null;
  isLoading: boolean;
}

export function useSession(): UseSessionResult {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const supa = getSupabaseClient();
    let cancelled = false;

    void supa.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setIsLoading(false);
    });

    const { data: subscription } = supa.auth.onAuthStateChange((_event, newSession) => {
      if (cancelled) return;
      setSession(newSession);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return { session, isLoading };
}
